/**
 * Payment grace period for TEAM accounts.
 *
 * Account.paidForTillDate is the billing date the last payment covered
 * (stored per billing-calendar-date.ts as billing-timezone midnight). A bill
 * can be raised against the account from that date; the account is blocked
 * once PAYMENT_GRACE_DAYS full days have elapsed AND an unpaid bill
 * (lastPaymentDataId) exists.
 *
 * Every consumer of the grace rule must use these helpers so the block instant
 * is the same everywhere: paidForTillDate + 7 x 24h, compared as an instant.
 */
import { AccountType } from 'app/API.service.custom';

export const PAYMENT_GRACE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

function toValidDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Instant from which the account is blocked (paidForTillDate + grace). */
export function getPaymentBlockInstant(paidForTillDate: Date | string | null | undefined): Date | null {
  const date = toValidDate(paidForTillDate);
  return date ? new Date(date.getTime() + PAYMENT_GRACE_DAYS * DAY_MS) : null;
}

/** True once the grace period after paidForTillDate has fully elapsed. */
export function isPastPaymentGrace(paidForTillDate: Date | string | null | undefined, now: Date = new Date()): boolean {
  const blockAt = getPaymentBlockInstant(paidForTillDate);
  return !!blockAt && now.getTime() >= blockAt.getTime();
}

/**
 * Days elapsed since a billing date, rounded up (negative while the date is
 * still in the future). Display-only: banners and "due soon" copy. Never use
 * this for the block decision; use isPastPaymentGrace / shouldBlockForPayment.
 */
export function getDaysOver(date: Date | string | null | undefined, now: Date = new Date()): number | null {
  const parsed = toValidDate(date);
  if (!parsed) return null;
  return Math.ceil((now.getTime() - parsed.getTime()) / DAY_MS);
}

export interface PaymentBlockableAccount {
  accountType?: string | null;
  paidForTillDate?: string | null;
  lastPaymentDataId?: string | null;
}

/** The single source of truth for "should this account be sent to /payment-pending". */
export function shouldBlockForPayment(account: PaymentBlockableAccount | null | undefined, now: Date = new Date()): boolean {
  if (!account || account.accountType !== AccountType.TEAM) return false;
  if (!account.lastPaymentDataId) return false;
  return isPastPaymentGrace(account.paidForTillDate, now);
}
